import { useState, useEffect } from 'react';
import { teachersAPI, bookingsAPI, attendanceAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Calendar, Clock, Users, MapPin, CheckCircle, Music, Grid3X3, List, ChevronLeft, ChevronRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import { formatTime, DAYS_OF_WEEK } from '../lib/utils';

const TeacherDashboard = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [viewMode, setViewMode] = useState('timetable'); // 'timetable' or 'list'
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(now.setDate(diff));
  });

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const res = await teachersAPI.getSchedule();
      setSchedules(res.data);
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const openAttendance = async (schedule) => {
    setSelectedSchedule(schedule);
    const today = new Date().toISOString().split('T')[0];
    
    try {
      const [studentsRes, attendanceRes] = await Promise.all([
        bookingsAPI.getBySchedule(schedule.id, today),
        attendanceAPI.get(schedule.id, today),
      ]);
      
      setStudents(studentsRes.data);
      
      const attendanceMap = {};
      attendanceRes.data.records?.forEach(r => {
        attendanceMap[r.booking_id] = r.present;
      });
      studentsRes.data.forEach(s => {
        if (attendanceMap[s.id] === undefined) {
          attendanceMap[s.id] = false;
        }
      });
      setAttendance(attendanceMap);
      setAttendanceOpen(true);
    } catch (error) {
      toast.error('Failed to load class roster');
    }
  };

  const toggleAttendance = (bookingId) => {
    setAttendance(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  const saveAttendance = async () => {
    if (!selectedSchedule) return;
    
    setSavingAttendance(true);
    const today = new Date().toISOString().split('T')[0];
    
    try {
      await attendanceAPI.mark({
        schedule_id: selectedSchedule.id,
        date: today,
        records: Object.entries(attendance).map(([bookingId, present]) => ({
          booking_id: bookingId,
          present: present,
        })),
      });
      toast.success('Attendance saved');
      setAttendanceOpen(false);
    } catch (error) {
      toast.error('Failed to save attendance');
    } finally {
      setSavingAttendance(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const today = new Date().getDay();  // JS: Sun=0..Sat=6 — matches schedule storage
  const todaySchedules = schedules.filter(s => s.day_of_week === today);
  
  // Time slots for timetable view (8am to 6pm)
  const timeSlots = Array.from({ length: 11 }, (_, i) => i + 8);
  
  const getScheduleStyle = (schedule) => {
    const startHour = parseInt(schedule.start_time.split(':')[0]);
    const startMin = parseInt(schedule.start_time.split(':')[1]);
    const endHour = parseInt(schedule.end_time.split(':')[0]);
    const endMin = parseInt(schedule.end_time.split(':')[1]);
    
    const top = ((startHour - 8) * 60 + startMin) * (60 / 60); // 60px per hour
    const height = ((endHour - startHour) * 60 + (endMin - startMin)) * (60 / 60);
    
    return { top: `${top}px`, height: `${Math.max(height, 30)}px` };
  };

  const getCategoryColor = (category) => {
    const colors = {
      ballet: 'bg-pink-500/20 border-pink-500/50 text-pink-300',
      drama: 'bg-purple-500/20 border-purple-500/50 text-purple-300',
      singing: 'bg-blue-500/20 border-blue-500/50 text-blue-300',
      dance: 'bg-green-500/20 border-green-500/50 text-green-300',
      'street-dance': 'bg-orange-500/20 border-orange-500/50 text-orange-300',
      default: 'bg-[#FF007F]/20 border-[#FF007F]/50 text-[#FF007F]'
    };
    return colors[category] || colors.default;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-20 md:pb-8">
      <Navbar />
      <Toaster position="top-right" richColors />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-teal-500 rounded-xl flex items-center justify-center">
                <Music className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
                  Teacher Dashboard
                </h1>
                <p className="text-white/60">Welcome, {user?.first_name}! Here's your schedule</p>
              </div>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-[#111] border border-[#333] rounded-lg p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('timetable')}
                className={viewMode === 'timetable' ? 'bg-[#FF007F] text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}
              >
                <Grid3X3 className="w-4 h-4 mr-1" />
                Timetable
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-[#FF007F] text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}
              >
                <List className="w-4 h-4 mr-1" />
                List
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-[#111] border-[#333]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/50">Today's Classes</p>
                  <p className="text-3xl font-bold text-green-400">{todaySchedules.length}</p>
                </div>
                <Calendar className="w-10 h-10 text-green-400/30" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#111] border-[#333]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/50">Weekly Classes</p>
                  <p className="text-3xl font-bold text-[#7000FF]">{schedules.length}</p>
                </div>
                <Grid3X3 className="w-10 h-10 text-[#7000FF]/30" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#111] border-[#333]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/50">Next Class</p>
                  <p className="text-lg font-bold text-[#FF007F] truncate">
                    {todaySchedules[0]?.class_name || 'No classes today'}
                  </p>
                </div>
                <Clock className="w-10 h-10 text-[#FF007F]/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Today's Classes - Quick Actions */}
        {todaySchedules.length > 0 && (
          <section className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-400" />
              Today's Classes
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {todaySchedules.map(schedule => (
                <Card key={schedule.id} className="bg-[#111] border-[#333] hover:border-green-500/50 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Today</Badge>
                      <span className="font-mono text-sm text-white/50">
                        {formatTime(schedule.start_time)}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold mb-2 text-white">
                      {schedule.class_name}
                    </h3>
                    <div className="space-y-2 text-sm text-white/60 mb-4">
                      <p className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                      </p>
                      <p className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {schedule.room_name}
                      </p>
                    </div>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => openAttendance(schedule)}
                      data-testid={`take-attendance-${schedule.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Take Attendance
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Timetable View */}
        {viewMode === 'timetable' && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-semibold text-white flex items-center gap-2">
                <Grid3X3 className="w-5 h-5 text-[#7000FF]" />
                Weekly Timetable
              </h2>
              
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigateWeek(-1)} className="text-white/60 hover:text-white">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-white/70 min-w-[200px] text-center">
                  {currentWeekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {
                    new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  }
                </span>
                <Button variant="ghost" size="sm" onClick={() => navigateWeek(1)} className="text-white/60 hover:text-white">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <Card className="bg-[#111] border-[#333] overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <div className="min-w-[800px]">
                    {/* Header Row */}
                    <div className="grid grid-cols-8 border-b border-[#333]">
                      <div className="p-3 text-center text-white/50 text-sm border-r border-[#333]">Time</div>
                      {getWeekDates().map((date, i) => {
                        const isToday = date.toDateString() === new Date().toDateString();
                        return (
                          <div key={i} className={`p-3 text-center border-r border-[#333] last:border-r-0 ${isToday ? 'bg-[#FF007F]/10' : ''}`}>
                            <div className={`text-sm font-medium ${isToday ? 'text-[#FF007F]' : 'text-white'}`}>
                              {DAYS_OF_WEEK[i]}
                            </div>
                            <div className={`text-xs ${isToday ? 'text-[#FF007F]/70' : 'text-white/50'}`}>
                              {date.getDate()}/{date.getMonth() + 1}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Time Grid */}
                    <div className="relative">
                      {timeSlots.map(hour => (
                        <div key={hour} className="grid grid-cols-8 border-b border-[#333]/50" style={{ height: '60px' }}>
                          <div className="p-2 text-xs text-white/40 border-r border-[#333] flex items-start justify-center">
                            {hour.toString().padStart(2, '0')}:00
                          </div>
                          {[0, 1, 2, 3, 4, 5, 6].map(dayIndex => (
                            <div key={dayIndex} className="border-r border-[#333]/30 last:border-r-0 relative" />
                          ))}
                        </div>
                      ))}
                      
                      {/* Schedule Blocks */}
                      {schedules.map(schedule => {
                        const style = getScheduleStyle(schedule);
                        const dayOffset = schedule.day_of_week;
                        const leftPercent = ((dayOffset + 1) / 8) * 100;
                        const widthPercent = (1 / 8) * 100;
                        
                        return (
                          <div
                            key={schedule.id}
                            className={`absolute rounded-md p-2 cursor-pointer hover:opacity-90 transition-opacity border ${getCategoryColor(schedule.category)}`}
                            style={{
                              ...style,
                              left: `calc(${leftPercent}% + 2px)`,
                              width: `calc(${widthPercent}% - 4px)`,
                            }}
                            onClick={() => openAttendance(schedule)}
                          >
                            <p className="text-xs font-medium truncate">{schedule.class_name}</p>
                            <p className="text-xs opacity-70">{formatTime(schedule.start_time)}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <section>
            <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <List className="w-5 h-5 text-[#7000FF]" />
              Weekly Schedule
            </h2>
            
            {loading ? (
              <Card className="bg-[#111] border-[#333]">
                <CardContent className="py-12 text-center">
                  <div className="animate-spin w-8 h-8 border-4 border-[#FF007F] border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-white/50">Loading schedule...</p>
                </CardContent>
              </Card>
            ) : schedules.length === 0 ? (
              <Card className="bg-[#111] border-[#333]">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-white mb-2">
                    No classes assigned
                  </h3>
                  <p className="text-white/60">You don't have any classes scheduled yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {DAYS_OF_WEEK.map((day, dayIndex) => {
                  const daySchedules = schedules.filter(s => s.day_of_week === dayIndex);
                  if (daySchedules.length === 0) return null;
                  
                  const isToday = new Date().getDay() === (dayIndex === 6 ? 0 : dayIndex + 1);
                  
                  return (
                    <Card key={day} className={`bg-[#111] border-[#333] ${isToday ? 'ring-2 ring-[#FF007F]' : ''}`}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                          {day}
                          {isToday && <Badge className="bg-[#FF007F] text-white">Today</Badge>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {daySchedules.map(schedule => (
                            <div 
                              key={schedule.id}
                              className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg border border-[#333] hover:border-[#FF007F]/50 transition-colors"
                            >
                              <div>
                                <p className="font-medium text-white">{schedule.class_name}</p>
                                <p className="text-sm text-white/50">
                                  {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)} • {schedule.room_name}
                                </p>
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openAttendance(schedule)}
                                className="border-[#333] text-white hover:bg-[#FF007F]/10 hover:border-[#FF007F]/50"
                              >
                                <Users className="w-4 h-4 mr-1" />
                                Roster
                              </Button>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>

      {/* Attendance Dialog */}
      <Dialog open={attendanceOpen} onOpenChange={setAttendanceOpen}>
        <DialogContent className="sm:max-w-lg bg-[#111] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">
              {selectedSchedule?.class_name} - Attendance
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-white/50 mb-4">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            
            {students.length === 0 ? (
              <p className="text-center py-8 text-white/50">
                No students enrolled for today's class.
              </p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {students.map(student => (
                  <div 
                    key={student.id}
                    className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg border border-[#333]"
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={student.id}
                        checked={attendance[student.id] || false}
                        onCheckedChange={() => toggleAttendance(student.id)}
                        className="border-[#333] data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                        data-testid={`attendance-${student.id}`}
                      />
                      <label htmlFor={student.id} className="cursor-pointer">
                        <p className="font-medium text-white">{student.student_name}</p>
                        <p className="text-sm text-white/50">Age {student.student_age}</p>
                      </label>
                    </div>
                    {attendance[student.id] && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Present</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttendanceOpen(false)} className="border-[#333] text-white hover:bg-[#222]">
              Cancel
            </Button>
            <Button 
              onClick={saveAttendance}
              disabled={savingAttendance || students.length === 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-attendance-btn"
            >
              {savingAttendance ? 'Saving...' : 'Save Attendance'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherDashboard;
